/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
// Global
import { useMemo } from 'react';
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { getTestProps } from 'lib/testing/utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import { findComponent } from 'lib/utils/object-utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const ServiceTabCardItem = (props)=>{
    // Support both direct fields and panel-prefixed fields
    const image = props?.fields?.image || props?.fields?.panelImage;
    const title = props?.fields?.title || props?.fields?.heading || props?.fields?.panelHeading;
    const description = props?.fields?.description || props?.fields?.panelDescription;
    const cardLink = props?.fields?.cardLink || props?.fields?.panelCTA;
    const { RenderingIdentifier } = props?.params || {};
    const isEditing = useIsEditing();
    const { sitecoreContext } = useSitecoreContext();
    // Get service tab item title for GTM events
    const { serviceTabItemTitle } = useMemo(()=>{
        let titleValue = '';
        const serviceTabItems = findComponent(sitecoreContext ?? {}, 'ServiceTabItem');
        for (const serviceTabItem of serviceTabItems){
            const dynamicPlaceholderId = serviceTabItem?.params?.DynamicPlaceholderId;
            const firstChildInPlaceholder = serviceTabItem?.placeholders?.[`servicetabcard-${dynamicPlaceholderId}`]?.[0];
            if (props?.rendering?.uid === firstChildInPlaceholder?.uid) {
                // Try to get title from tab item fields
                const tabItemFields = serviceTabItem?.fields;
                titleValue = tabItemFields?.title?.value || tabItemFields?.tabTitle?.value || '';
                break;
            }
        }
        return {
            serviceTabItemTitle: titleValue
        };
    }, [
        sitecoreContext,
        props?.rendering?.uid
    ]);
    const styles = parseStyleParams(props.params, [
        'cta1'
    ]);
    const { container, imageContainer, imageWrapper, contentWrapper, content, titleTV, descriptionTV, divider, footer, cta } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("article", {
        className: container(),
        "data-component": "authorable/shared/lists/ServiceTabCardItem",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`service-tab-card-item-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: imageContainer(),
                children: isEditing ? /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: image,
                    className: imageWrapper(),
                    ...getTestProps(`image`)
                }) : /*#__PURE__*/ _jsx(_Fragment, {
                    children: image?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: image,
                        className: imageWrapper(),
                        ...getTestProps(`image`)
                    }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: {
                            value: {
                                src: '/assets/profile_placeholder.png',
                                alt: 'No image'
                            }
                        },
                        className: imageWrapper(),
                        ...getTestProps(`related-profile-image-placeholder`)
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: contentWrapper(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: content(),
                        children: [
                            title && /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: titleTV(),
                                field: title,
                                tag: "h2",
                                ...getTestProps(`title`)
                            }),
                            description && /*#__PURE__*/ _jsx(RichTextWrapper, {
                                className: descriptionTV(),
                                field: description,
                                ...getTestProps(`description`)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: divider(),
                                ...getTestProps(`divider`)
                            })
                        ]
                    }),
                    cardLink && /*#__PURE__*/ _jsx("div", {
                        className: footer(),
                        children: /*#__PURE__*/ _jsx(LinkWrapper, {
                            ctaComponentClass: "default",
                            className: cta(),
                            ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                            field: cardLink,
                            remixIcon: "arrow-right-line",
                            remixIconSize: "md",
                            ...getTestProps(`card-link`),
                            gtmEvent: createCTAClickGTMEvent({
                                header: serviceTabItemTitle,
                                ctaText: cardLink?.value?.text,
                                ctaType: 'blue',
                                componentName: 'Tabbed Cards',
                                ctaLink: cardLink?.value?.href
                            })
                        })
                    })
                ]
            })
        ]
    });
};
export const Default = withStandardComponentWrapper(ServiceTabCardItem);
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [
            'flex',
            'flex-col',
            'lg:flex-row',
            'w-full',
            'h-full',
            'overflow-hidden',
            'rounded-general-border-radius-card',
            'bg-component-card-item-surface',
            'border-component-card-item-border',
            'border-component-card-item-border-width',
            'border-solid',
            'shadow-[0_0_8px_0_rgba(0,0,0,0.10)]'
        ],
        imageContainer: [
            'flex',
            'flex-col',
            'lg:flex-[1_0_0]',
            'items-start',
            'pb-component-card-item-portrait-image-padding-bottom',
            'pt-component-card-item-portrait-image-padding-top',
            'px-component-card-item-portrait-image-padding-x',
            'lg:pl-component-article-card-landscape-image-padding-y',
            'lg:pr-0',
            'lg:py-component-card-item-landscape-image-padding-y',
            'lg:h-full',
            'min-h-px',
            'min-w-px',
            'relative',
            'w-full',
            'lg:max-w-[478px]'
        ],
        imageWrapper: [
            'lg:flex-[1_0_0]',
            'aspect-[286/161]',
            'lg:aspect-auto',
            'min-h-px',
            'min-w-px',
            'relative',
            'rounded-tl-component-card-item-image-radius',
            'rounded-tr-component-card-item-image-radius',
            'lg:rounded-bl-component-card-item-image-radius',
            'lg:rounded-tl-component-card-item-image-radius',
            'lg:rounded-tr-none',
            'w-full',
            'lg:h-full',
            'object-cover'
        ],
        contentWrapper: [
            'flex',
            'lg:flex-[1_0_0]',
            'flex-col',
            'gap-component-video-card-portrait-content-button-margin-top',
            'lg:gap-spacing-spacing-24',
            'items-start',
            'pb-component-card-item-portrait-content-padding-bottom',
            'pt-component-card-item-portrait-content-padding-top',
            'px-component-card-item-portrait-content-padding-x',
            'lg:px-component-card-item-landscape-content-1-col-padding-x',
            'lg:py-component-card-item-landscape-content-1-col-padding-y',
            'lg:pb-component-card-item-landscape-content-1-col-padding-y',
            'lg:pt-component-card-item-landscape-content-1-col-padding-y',
            'min-h-px',
            'min-w-px',
            'relative',
            'w-full',
            'lg:justify-center'
        ],
        content: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'items-start',
            'relative',
            'shrink-0',
            'w-full',
            'text-left'
        ],
        titleTV: [
            'font-typography-font-family-header',
            'font-typography-font-weight-small',
            'leading-typography-line-height-medium',
            'min-w-full',
            'relative',
            'shrink-0',
            'text-component-card-item-title',
            'text-typography-font-size-medium',
            'w-min'
        ],
        descriptionTV: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'leading-typography-line-height-body-medium',
            'min-w-full',
            'relative',
            'shrink-0',
            'text-component-card-item-body',
            'text-typography-font-size-body-medium',
            'w-min'
        ],
        divider: [
            'bg-color-core-color-3-Coral',
            'h-[2px]',
            'shrink-0',
            'w-[100px]'
        ],
        footer: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8',
            'items-start',
            'justify-center',
            'relative',
            'shrink-0',
            'w-full'
        ],
        cta: [
            'flex',
            'gap-spacing-spacing-8',
            'h-[52px]',
            'items-center',
            'justify-center',
            'px-spacing-spacing-24',
            'py-spacing-spacing-16',
            'relative',
            'shrink-0',
            '!w-auto'
        ]
    }
});
